import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ConfirmModal } from '@/components/ConfirmModal';
import { Skeleton } from '@/components/Skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { discountCellText } from '@/lib/discount-display';

import { useClearViewHistory, useViewHistory } from './DashboardHistoryPage.data';

export function DashboardHistoryPage() {
  const { t } = useTranslation();
  const auth = useAuth();
  const userId = auth.status === 'authenticated' ? auth.user.id : null;

  const { state, refresh } = useViewHistory();
  const { clear } = useClearViewHistory(userId);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleClear = async () => {
    setConfirmOpen(false);
    try {
      const ok = await clear();
      if (!ok) return;
      toast.success(t('buyer.history.clearedToast'));
      refresh();
    } catch {
      toast.error(t('buyer.errors.generic'));
    }
  };

  const isSuccessEmpty = state.status === 'success' && state.history.length === 0;
  const isSuccessFilled = state.status === 'success' && state.history.length > 0;

  return (
    <>
      <header className="buyer-page-head">
        <div>
          <h1 className="buyer-page-head__title">{t('buyer.history.title')}</h1>
        </div>
        {isSuccessFilled ? (
          <button
            type="button"
            className="buyer-history-clear"
            onClick={() => setConfirmOpen(true)}
          >
            {t('buyer.history.clear')}
          </button>
        ) : null}
      </header>

      {state.status === 'loading' ? (
        <div style={{ display: 'grid', gap: 12 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height={56} />
          ))}
        </div>
      ) : null}

      {state.status === 'error' ? (
        <div className="buyer-empty" role="alert">
          <h2 className="buyer-empty__title">{t('buyer.history.errorTitle')}</h2>
          <p className="buyer-empty__lead">{t('buyer.history.errorLead')}</p>
          <button type="button" className="m5-btn m5-btn--primary" onClick={refresh}>
            {t('buyer.history.retry')}
          </button>
        </div>
      ) : null}

      {isSuccessEmpty ? (
        <div className="buyer-empty">
          <h2 className="buyer-empty__title">{t('buyer.history.emptyTitle')}</h2>
        </div>
      ) : null}

      {isSuccessFilled ? (
        <div style={{ overflowX: 'auto' }}>
          <table className="buyer-data-table">
            <thead>
              <tr>
                <th>{t('buyer.history.headers.property')}</th>
                <th>{t('buyer.history.headers.details')}</th>
                <th>{t('buyer.history.headers.viewed')}</th>
              </tr>
            </thead>
            <tbody>
              {state.history.map((row) => (
                <tr key={row.id}>
                  <td>
                    <div className="buyer-data-table__title">{row.dealTitle}</div>
                    <div className="buyer-data-table__zone">{row.zone}</div>
                  </td>
                  <td>
                    <div className="buyer-data-table__price">{row.priceLabel}</div>
                    <div className="buyer-data-table__discount">
                      {discountCellText(row.dldConfidence, row.dldScope, row.discountPct)}
                    </div>
                  </td>
                  <td className="buyer-data-table__zone">{row.viewedAt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {confirmOpen && (
        <ConfirmModal
          title={t('buyer.history.modal.title')}
          body={t('buyer.history.modal.body')}
          confirmLabel={t('buyer.history.modal.confirm')}
          cancelLabel={t('buyer.history.modal.cancel')}
          danger
          onConfirm={() => {
            void handleClear();
          }}
          onCancel={() => setConfirmOpen(false)}
        />
      )}
    </>
  );
}
